/**
 * `ListPage` - the /accounts list view.
 *
 * Composes:
 *   FilterBar       (URL-driven; submit -> setSearchParams)
 *   AccountTable    (flat list of pages; overrides for loading / empty / error)
 *   Pagination      (Next-only at M1; cursor follows lastPage.next_cursor)
 *   SessionExpiredDialog (mounts on 401 / silent-refresh failure)
 *
 * State flows:
 *   URL  -> AccountsUrlState -> useAccountsQuery (TanStack useInfiniteQuery)
 *   User -> FilterBar -> setSearchParams -> URL (re-runs the above)
 *
 * Why the page composes its own client rather than reading from a
 * provider: the BFF client wraps the (per-user) MSAL access-token
 * acquirer, which is cheap to build but expensive to memoise across
 * routes. Keeping construction at the page level means each navigation
 * gets a fresh client bound to the live MSAL state without context
 * gymnastics. We can promote to a provider in PR-E2 when the detail page
 * also needs it.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useMsal } from '@azure/msal-react'
import { useSearchParams } from 'react-router-dom'

import { AccountTable } from '../../components/account-table'
import { EmptyState } from '../../components/empty-state'
import { ErrorState } from '../../components/error-state'
import { Pagination } from '../../components/pagination'
import { SessionExpiredDialog } from '../../components/session-expired-dialog'
import { SkeletonRows } from '../../components/skeleton-rows'
import { apiBaseUrl, apiTokenScopes } from '../../auth/msal-config'
import { createBffClient, BffError, type BffClient } from '../../lib/bff-client'
import { useBffError } from '../../lib/use-bff-error'
import { FilterBar } from './filter-bar'
import { useAccountsQuery } from './use-accounts-query'
import { isFiltered, parseAccountsUrlState, serialiseAccountsUrlState } from './url-state'

export interface ListPageProps {
  /** Test-only override; production passes nothing and the page builds
   *  its own client bound to MSAL. */
  readonly client?: BffClient
}

export function ListPage({ client: clientOverride }: ListPageProps = {}): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const state = useMemo(() => parseAccountsUrlState(searchParams), [searchParams])

  const filterBarRef = useRef<HTMLDivElement | null>(null)

  const { instance, accounts: msalAccounts } = useMsal()
  const bffError = useBffError()

  const client = useMemo<BffClient>(() => {
    if (clientOverride) return clientOverride
    return createBffClient({
      baseUrl: apiBaseUrl,
      acquireToken: async () => {
        const account = msalAccounts[0]
        const result = await instance.acquireTokenSilent(
          account ? { scopes: apiTokenScopes, account } : { scopes: apiTokenScopes },
        )
        return result.accessToken
      },
    })
  }, [clientOverride, instance, msalAccounts])

  const query = useAccountsQuery({ client, state })

  // Centralised error routing - 401 -> dialog, 403 -> /access-denied.
  useEffect(() => {
    if (query.error) {
      bffError.handle(query.error)
    }
  }, [query.error, bffError])

  const items = useMemo(() => {
    if (!query.data) return []
    return query.data.pages.flatMap((page) => page.items)
  }, [query.data])

  const focusFilterBar = useCallback(() => {
    const input = filterBarRef.current?.querySelector<HTMLInputElement>('input:not([disabled])')
    input?.focus()
  }, [])

  const handleApply = useCallback(
    (next: { extAccountId: string | null; legacyId: string | null }) => {
      const params = serialiseAccountsUrlState({
        extAccountId: next.extAccountId,
        legacyId: next.legacyId,
        limit: state.limit,
      })
      setSearchParams(params, { replace: false })
    },
    [setSearchParams, state.limit],
  )

  const handleClear = useCallback(() => {
    const params = serialiseAccountsUrlState({
      extAccountId: null,
      legacyId: null,
      limit: state.limit,
    })
    setSearchParams(params, { replace: false })
  }, [setSearchParams, state.limit])

  const correlationId = query.error instanceof BffError ? query.error.correlationId : null

  // Decide what to render inside the table body.
  let bodyOverride: React.ReactNode = undefined
  if (query.isPending) {
    bodyOverride = <SkeletonRows count={8} />
  } else if (query.isError && items.length === 0) {
    bodyOverride = (
      <ErrorState
        correlationId={correlationId}
        onRetry={() => {
          void query.refetch()
        }}
      />
    )
  } else if (items.length === 0) {
    bodyOverride = (
      <EmptyState
        filtered={isFiltered(state)}
        filterField={state.extAccountId !== null ? 'account number' : 'legacy id'}
        onClearFilter={handleClear}
      />
    )
  }

  // `min-h-0 flex-1` below continues the height chain from AppShell's
  // <main> down to the table's own scroll container. Every link in that
  // chain needs it: a flex child defaults to `min-height: auto`, which
  // refuses to shrink below its content, so without it the card grows to
  // fit all 50 rows and the page scrolls instead of the table - taking the
  // sticky column headers off screen with it.
  return (
    <section
      aria-labelledby="members-heading"
      className="flex min-h-0 flex-1 flex-col gap-murmuration-4"
    >
      <header className="flex items-end justify-between">
        <h1 id="members-heading" tabIndex={-1} className="text-murmuration-h1 text-text-primary">
          Members
        </h1>
        {/* The design handoff puts a "New member" primary button here. There is
            no create path at M1 - the projection is read-only - so the slot is
            left empty rather than shipping a button that cannot work. */}
      </header>

      <div ref={filterBarRef}>
        <FilterBar
          initialExtAccountId={state.extAccountId}
          initialLegacyId={state.legacyId}
          onApply={handleApply}
          onClear={handleClear}
        />
      </div>

      <AccountTable
        accounts={items}
        busy={query.isFetching}
        bodyOverride={bodyOverride}
        onEscape={focusFilterBar}
        footer={
          items.length > 0 ? (
            <Pagination
              currentCount={items.length}
              canFetchMore={Boolean(query.hasNextPage)}
              isFetchingMore={Boolean(query.isFetchingNextPage)}
              onNext={() => {
                void query.fetchNextPage()
              }}
            />
          ) : undefined
        }
      />

      <SessionExpiredDialog
        open={bffError.sessionExpiredOpen}
        onDismiss={bffError.dismissSessionExpired}
      />
    </section>
  )
}
